import { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

export default function Navbar() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  
  // Check if we're on a non-home page
  const isNonHomePage = location.pathname !== '/';

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (sectionId: string) => {
    // If we're not on the home page, navigate to home first
    if (isNonHomePage) {
      navigate('/');
      // Wait for navigation to complete, then scroll
      setTimeout(() => {
        const element = document.getElementById(sectionId);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth' });
        }
      }, 100);
    } else {
      const element = document.getElementById(sectionId);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
      }
    }
  };

  // Force white background with shadow on non-home pages
  const navbarClasses = isNonHomePage 
    ? 'bg-white shadow-md'
    : isScrolled 
      ? 'bg-white shadow-md' 
      : 'bg-transparent';

  const textClasses = isNonHomePage || isScrolled
    ? 'text-gray-900 hover:text-teal-600'
    : 'text-white hover:text-teal-200';

  const logoClasses = isNonHomePage || isScrolled
    ? 'bg-gradient-to-br from-gray-900 to-gray-700 shadow-lg'
    : 'bg-gradient-to-br from-teal-500 to-teal-700 shadow-xl';

  const buttonClasses = isNonHomePage || isScrolled
    ? 'bg-teal-600 text-white hover:bg-teal-700'
    : 'bg-white text-teal-600 hover:bg-teal-50';

  const mobileIconClasses = isNonHomePage || isScrolled
    ? 'text-gray-900'
    : 'text-white';

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${navbarClasses}`}
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        <div className="flex items-center justify-between h-20">
          <div className="flex items-center space-x-3 cursor-pointer" onClick={() => navigate('/')}>
            <div className={`w-12 h-12 rounded-lg flex items-center justify-center transition-all duration-300 relative overflow-hidden ${logoClasses}`}>
              <div className="absolute inset-0 bg-gradient-to-tr from-transparent via-white/10 to-transparent"></div>
              <span className="font-bold text-2xl transition-colors relative z-10 text-white" style={{ fontFamily: 'Playfair Display, serif' }}>U</span>
            </div>
            <span
              className={`text-xl font-bold transition-colors ${isNonHomePage || isScrolled ? 'text-gray-900' : 'text-white'}`}
              style={{ fontFamily: 'Playfair Display, serif' }}
            >
              Urban Spaces
            </span>
          </div>

          <div className="hidden lg:flex items-center space-x-8">
            <a
              onClick={() => scrollToSection('services')}
              className={`text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${textClasses}`}
            >
              Services
            </a>
            <a
              onClick={() => scrollToSection('portfolio')}
              className={`text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${textClasses}`}
            >
              Portfolio
            </a>
            <a
              onClick={() => scrollToSection('testimonials')}
              className={`text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${textClasses}`}
            >
              Testimonials
            </a>
            <a
              onClick={() => scrollToSection('blog')}
              className={`text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${textClasses}`}
            >
              Blog
            </a>
            <a
              onClick={() => scrollToSection('booking')}
              className={`px-6 py-2.5 rounded-lg font-medium transition-all text-sm cursor-pointer whitespace-nowrap ${buttonClasses}`}
            >
              Book Consultation
            </a>
          </div>

          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className={`lg:hidden w-10 h-10 flex items-center justify-center ${mobileIconClasses}`}
          >
            <i className={`${isMobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'} text-2xl`}></i>
          </button>
        </div>
      </div>

      {isMobileMenuOpen && (
        <div className="lg:hidden bg-white border-t border-gray-200">
          <div className="px-6 py-4 space-y-3">
            <a
              onClick={(e) => {
                e.preventDefault();
                scrollToSection('services');
                setIsMobileMenuOpen(false);
              }}
              className="block text-gray-900 hover:text-teal-600 font-medium cursor-pointer"
            >
              Services
            </a>
            <a
              onClick={(e) => {
                e.preventDefault();
                scrollToSection('portfolio');
                setIsMobileMenuOpen(false);
              }}
              className="block text-gray-900 hover:text-teal-600 font-medium cursor-pointer"
            >
              Portfolio
            </a>
            <a
              onClick={(e) => {
                e.preventDefault();
                scrollToSection('testimonials');
                setIsMobileMenuOpen(false);
              }}
              className="block text-gray-900 hover:text-teal-600 font-medium cursor-pointer"
            >
              Testimonials
            </a>
            <a
              onClick={(e) => {
                e.preventDefault();
                scrollToSection('blog');
                setIsMobileMenuOpen(false);
              }}
              className="block text-gray-900 hover:text-teal-600 font-medium cursor-pointer"
            >
              Blog
            </a>
            <a
              onClick={(e) => {
                e.preventDefault();
                scrollToSection('booking');
                setIsMobileMenuOpen(false);
              }}
              className="block bg-teal-600 text-white px-6 py-2.5 rounded-lg text-center font-medium hover:bg-teal-700 transition-colors cursor-pointer"
            >
              Book Consultation
            </a>
          </div>
        </div>
      )}
    </nav>
  );
}
